import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Alert,
  CircularProgress,
  Fab,
  Tooltip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText as MuiListItemText,
  Autocomplete,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  CheckCircle as CheckIcon,
  Delete as DeleteIcon,
  Timeline as TimelineIcon,
  Speed as SpeedIcon,
  TrendingDown as TrendingDownIcon,
  Assignment as AssignmentIcon,
  Edit as EditIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import Layout from '../components/Layout/Layout';
import SprintMetricsCard from '../components/Dashboard/SprintMetricsCard';
import SprintBurndownChart from '../components/Sprint/SprintBurndownChart';
import SprintTimelineView from '../components/Sprint/SprintTimelineView';
import apiService from '../services/api';
import { Sprint, Epic, Project, UserRole } from '../types';
import { useAuth } from '../contexts/AuthContext';

interface ProjectWithSprints {
  id: string;
  name: string;
  sprints: Sprint[];
}

interface SprintWithBoard extends Sprint {
  board?: {
    id: string;
    name: string;
  };
}

const Sprints: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId') || undefined;
  
  // Check if user can manage sprints (Manager, Admin, etc.)
  const canManageSprints = !!user && [
    UserRole.MANAGER,
    UserRole.SUPER_MANAGER,
    UserRole.ADMIN,
    UserRole.ROOT_USER,
    UserRole.PROJECT_MANAGER,
    UserRole.PRODUCT_OWNER,
    UserRole.SCRUM_MASTER
  ].includes(user.role);
  
  console.log('Sprints component rendered with projectId:', projectId);

  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [projectsWithSprints, setProjectsWithSprints] = useState<ProjectWithSprints[]>([]);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [showCompletedSprints, setShowCompletedSprints] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState<ProjectWithSprints | null>(null);

  // Sprint creation
  const [openCreateSprintModal, setOpenCreateSprintModal] = useState(false);
  const [sprintForm, setSprintForm] = useState({
    name: '',
    goal: '',
    startDate: '',
    endDate: '',
    plannedStoryPoints: 0,
    epicIds: [] as string[]
  });
  const [createSprintLoading, setCreateSprintLoading] = useState(false);
  const [selectedProjectIdForCreation, setSelectedProjectIdForCreation] = useState<string>(projectId || '');

  // Sprint details
  const [selectedSprint, setSelectedSprint] = useState<Sprint | null>(null);
  const [openSprintDetailsModal, setOpenSprintDetailsModal] = useState(false);
  const [sprintDetailsTab, setSprintDetailsTab] = useState(0);

  // Board assignment
  const [openBoardAssignmentModal, setOpenBoardAssignmentModal] = useState(false);
  const [boards, setBoards] = useState<any[]>([]);
  const [selectedBoardId, setSelectedBoardId] = useState<string>('');
  const [boardAssignmentLoading, setBoardAssignmentLoading] = useState(false);

  // Features state
  const [features, setFeatures] = useState<Epic[]>([]);
  const [featuresLoading, setFeaturesLoading] = useState(false);

  useEffect(() => {
    // Always have projects list ready for selector
    loadAccessibleProjects();

    if (projectId) {
      // Load sprints for specific project (when accessed via query param)
      loadSprints();
      loadBoards();
      loadFeatures();
    } else {
      // Load all sprints from all projects (when accessed via nav)
      loadAllProjectsSprints();
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      loadSprints();
    } else {
      loadAllProjectsSprints();
    }
  }, [showCompletedSprints, projectId]);

  const loadAllProjectsSprints = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get all projects the user has access to (use getUserProjects for regular users)
      const isAdmin = user?.role && ['admin', 'super_manager'].includes(user.role);
      const projects = isAdmin 
        ? await apiService.getProjects()
        : await apiService.getUserProjects();
      console.log('Loaded projects:', projects);
      
      // Load sprints for each project
      const projectsWithSprintsData: ProjectWithSprints[] = [];
      
      for (const project of projects) {
        try {
          const projectSprints = await apiService.getProjectSprints(project.id, showCompletedSprints);
          projectsWithSprintsData.push({
            id: project.id,
            name: project.name,
            sprints: projectSprints
          });
        } catch (err) {
          console.error(`Failed to load sprints for project ${project.name}:`, err);
        }
      }
      
      setProjectsWithSprints(projectsWithSprintsData);
      console.log('Loaded projects with sprints:', projectsWithSprintsData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load projects and sprints');
    } finally {
      setLoading(false);
    }
  };

  const loadAccessibleProjects = async () => {
    try {
      const isAdmin = user?.role && ['admin', 'super_manager'].includes(user.role);
      const projects = isAdmin 
        ? await apiService.getProjects()
        : await apiService.getUserProjects();
      setAllProjects(projects || []);
    } catch (err) {
      // Non-blocking
      console.warn('Failed to load projects list for selector', err);
    }
  };

  const loadSprints = async () => {
    if (!projectId) {
      console.log('No projectId provided to Sprints component');
      setLoading(false);
      setError('No project selected. Please select a project to view sprints.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      // Load only active and planning sprints by default, completed sprints only if showCompletedSprints is true
      const data = await apiService.getProjectSprints(projectId, showCompletedSprints);
      setSprints(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load sprints');
    } finally {
      setLoading(false);
    }
  };

  const loadBoards = async () => {
    try {
      const boardsData = await apiService.getBoardsForProject(projectId!);
      setBoards(boardsData);
    } catch (error) {
      console.error('Error loading boards:', error);
    }
  };

  const loadFeatures = async () => {
    if (!projectId) return;
    try {
      setFeaturesLoading(true);
      const data = await apiService.getEpics(projectId);
      setFeatures(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load features');
    } finally {
      setFeaturesLoading(false);
    }
  };

  const loadFeaturesForProject = async (pid: string) => {
    if (!pid) return;
    try {
      setFeaturesLoading(true);
      const data = await apiService.getEpics(pid);
      setFeatures(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load features');
    } finally {
      setFeaturesLoading(false);
    }
  };

  const handleCreateSprint = () => {
    if (projectId) {
      setSelectedProjectIdForCreation(projectId);
    } else {
      setSelectedProjectIdForCreation('');
      // Ensure projects are loaded for selector
      if (allProjects.length === 0) {
        loadAccessibleProjects();
      }
    }
    setOpenCreateSprintModal(true);
  };

  const handleCloseCreateSprintModal = () => {
    setOpenCreateSprintModal(false);
    setSprintForm({
      name: '',
      goal: '',
      startDate: '',
      endDate: '',
      plannedStoryPoints: 0,
      epicIds: []
    });
  };

  const handleSaveSprint = async () => {
    const effectiveProjectId = projectId || selectedProjectIdForCreation;
    if (!sprintForm.name || !sprintForm.startDate || !sprintForm.endDate || !effectiveProjectId) return;

    try {
      setCreateSprintLoading(true);
      await apiService.createSprint({
        ...sprintForm,
        projectId: effectiveProjectId
      });
      if (projectId) {
        await loadSprints();
      } else {
        await loadAllProjectsSprints();
      }
      handleCloseCreateSprintModal();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create sprint');
    } finally {
      setCreateSprintLoading(false);
    }
  };

  const handleStartSprint = async (sprintId: string) => {
    try {
      await apiService.startSprint(sprintId);
      if (projectId) {
        await loadSprints();
      } else {
        await loadAllProjectsSprints();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to start sprint');
    }
  };

  const handleCompleteSprint = async (sprintId: string) => {
    try {
      await apiService.completeSprint(sprintId);
      if (projectId) {
        await loadSprints();
      } else {
        await loadAllProjectsSprints();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to complete sprint');
    }
  };

  const handleViewSprintDetails = (sprint: SprintWithBoard) => {
    setSelectedSprint(sprint);
    setOpenSprintDetailsModal(true);
  };

  const handleAssignBoard = (sprint: SprintWithBoard) => {
    setSelectedSprint(sprint);
    setSelectedBoardId(sprint.board?.id || '');
    setOpenBoardAssignmentModal(true);
  };

  const handleCloseBoardAssignmentModal = () => {
    setOpenBoardAssignmentModal(false);
    setSelectedSprint(null);
    setSelectedBoardId('');
  };

  const handleBoardAssignmentSubmit = async () => {
    if (!selectedSprint || !selectedBoardId) return;

    try {
      setBoardAssignmentLoading(true);
      await apiService.assignSprintToBoard(selectedSprint.id, selectedBoardId);
      await loadSprints();
      handleCloseBoardAssignmentModal();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to assign sprint to board');
    } finally {
      setBoardAssignmentLoading(false);
    }
  };

  const getSprintStatusColor = (state: string) => {
    switch (state) {
      case 'planning': return 'warning';
      case 'active': return 'success';
      case 'completed': return 'info';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getSprintStatusIcon = (state: string) => {
    switch (state) {
      case 'planning': return <PauseIcon />;
      case 'active': return <PlayIcon />;
      case 'completed': return <CheckIcon />;
      case 'cancelled': return <DeleteIcon />;
      default: return <TimelineIcon />;
    }
  };

  const getActiveSprints = () => sprints.filter(sprint => sprint.state === 'active');
  const getPlanningSprints = () => sprints.filter(sprint => sprint.state === 'planning');
  const getCompletedSprints = () => sprints.filter(sprint => sprint.state === 'completed');

  // Filter projects based on selected project
  const getFilteredProjects = (projects: ProjectWithSprints[]) => {
    if (!selectedProject) {
      return projects;
    }
    return projects.filter(project => project.id === selectedProject.id);
  };

  // Filter sprints based on search term
  const getFilteredSprints = (sprints: Sprint[]) => {
    if (!searchTerm) {
      return sprints;
    }
    return sprints.filter(sprint => 
      sprint.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (sprint.goal && sprint.goal.toLowerCase().includes(searchTerm.toLowerCase())) ||
      sprint.state.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Get total filtered sprints count
  const getTotalFilteredSprints = () => {
    const filteredProjects = getFilteredProjects(projectsWithSprints);
    return filteredProjects.reduce((total, project) => {
      const filteredSprints = getFilteredSprints(project.sprints);
      return total + filteredSprints.length;
    }, 0);
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Modern Professional Header */}
      <Box sx={{ 
        mb: 3,
        background: (theme) => theme.palette.mode === 'dark' 
          ? 'rgba(15, 23, 42, 0.95)' 
          : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        boxShadow: (theme) => theme.palette.mode === 'dark' 
          ? '0 4px 20px rgba(0, 0, 0, 0.3)' 
          : '0 4px 20px rgba(0, 0, 0, 0.1)',
        borderRadius: 2,
        p: 3,
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center' 
      }}>
          <Typography variant="h4" component="h1">
            Sprints
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => setShowCompletedSprints(!showCompletedSprints)}
              sx={{ minWidth: 'auto' }}
            >
              {showCompletedSprints ? 'Hide' : 'Show'} Completed
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateSprint}
            >
              Create Sprint
            </Button>
          </Box>
        </Box>

        {/* Conditional rendering based on projectId */}
        {projectId ? (
          <>
            {/* Tabs for single project view */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                <Tab label="Active" />
                <Tab label="Planning" />
                {showCompletedSprints && <Tab label="Completed" />}
              </Tabs>
            </Box>
          </>
        ) : (
          <>
            {/* All projects view header */}
            <Typography variant="h6" sx={{ mb: 3, color: 'text.secondary' }}>
              {getTotalFilteredSprints()} sprints across {getFilteredProjects(projectsWithSprints).length} projects
            </Typography>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Search sprints..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={projectsWithSprints}
                    getOptionLabel={(option) => option.name}
                    value={selectedProject}
                    onChange={(event, newValue) => {
                      setSelectedProject(newValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select project..."
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {option.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.sprints.length} sprints
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    clearOnEscape
                    noOptionsText="No projects found"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    variant="outlined"
                    startIcon={<FilterIcon />}
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedProject(null);
                    }}
                    fullWidth
                  >
                    Clear Filters
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}

        {/* Content based on view mode */}
        {projectId ? (
          <>
            {/* Single project tabs content */}
            {/* Active Sprints Tab */}
            {activeTab === 0 && (
          <Grid container spacing={3}>
            {getActiveSprints().map((sprint) => (
              <Grid item xs={12} md={6} lg={4} key={sprint.id}>
                <SprintMetricsCard
                  sprintId={sprint.id}
                  sprint={sprint}
                  onViewDetails={(sprintId) => {
                    // Handle sprint details view
                    console.log('View sprint details:', sprintId);
                  }}
                />
              </Grid>
            ))}
            {getActiveSprints().length === 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">
                    No active sprints
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Create a new sprint or start a planning sprint to get started.
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}

        {/* Planning Sprints Tab */}
        {activeTab === 1 && (
          <Grid container spacing={3}>
            {getPlanningSprints().map((sprint) => (
              <Grid item xs={12} md={6} lg={4} key={sprint.id}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {sprint.name}
                    </Typography>
                    <Chip
                      icon={getSprintStatusIcon(sprint.state)}
                      label={sprint.state}
                      color={getSprintStatusColor(sprint.state) as any}
                      size="small"
                    />
                  </Box>
                  
                  {sprint.goal && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {sprint.goal}
                    </Typography>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(sprint.startDate).toLocaleDateString()} - {new Date(sprint.endDate).toLocaleDateString()}
                    </Typography>
                  </Box>

                  {sprint.board && (
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        icon={<AssignmentIcon />}
                        label={`Board: ${sprint.board.name}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleViewSprintDetails(sprint)}
                    >
                      View Details
                    </Button>
                    {sprint.board && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => window.location.href = `/board/${projectId}?boardId=${sprint.board!.id}`}
                      >
                        View Board
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      onClick={() => handleAssignBoard(sprint)}
                    >
                      {sprint.board ? 'Change Board' : 'Assign Board'}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={() => handleStartSprint(sprint.id)}
                    >
                      Start Sprint
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
            {getPlanningSprints().length === 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">
                    No planning sprints
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Create a new sprint to start planning.
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        )}

        {/* Completed Sprints Tab */}
        {activeTab === 2 && showCompletedSprints && (
          <Grid container spacing={3}>
            {getCompletedSprints().map((sprint) => (
              <Grid item xs={12} md={6} lg={4} key={sprint.id}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {sprint.name}
                    </Typography>
                    <Chip
                      icon={getSprintStatusIcon(sprint.state)}
                      label={sprint.state}
                      color={getSprintStatusColor(sprint.state) as any}
                      size="small"
                    />
                  </Box>
                  
                  {sprint.goal && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {sprint.goal}
                    </Typography>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(sprint.startDate).toLocaleDateString()} - {new Date(sprint.endDate).toLocaleDateString()}
                    </Typography>
                  </Box>

                  {(sprint.plannedStoryPoints || 0) > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Final Progress: {sprint.progressPercentage || 0}% ({sprint.completedStoryPoints || 0}/{sprint.plannedStoryPoints} SP)
                      </Typography>
                    </Box>
                  )}

                  {sprint.board && (
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        icon={<AssignmentIcon />}
                        label={`Board: ${sprint.board.name}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleViewSprintDetails(sprint)}
                    >
                      View Details
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            ))}
            {getCompletedSprints().length === 0 && (
              <Grid item xs={12}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary">
                    No completed sprints
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Complete sprints to see them here.
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
            )}
          </>
        ) : (
          <>
            {/* All projects view */}
            {getFilteredProjects(projectsWithSprints).length === 0 ? (
              <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  No projects match the current filters
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Try adjusting your search criteria or clear the filters.
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={3}>
                {getFilteredProjects(projectsWithSprints).map((project) => {
                  const filteredSprints = getFilteredSprints(project.sprints);
                  return (
                    <Grid item xs={12} key={project.id}>
                      <Paper sx={{ p: 3 }}>
                        <Typography variant="h5" sx={{ mb: 2, color: 'primary.main', fontWeight: 'bold' }}>
                          {project.name}
                        </Typography>
                        
                        {filteredSprints.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            {project.sprints.length === 0 
                              ? 'No sprints in this project' 
                              : 'No sprints match the current filters'
                            }
                          </Typography>
                        ) : (
                          <Grid container spacing={2}>
                            {filteredSprints.map((sprint) => (
                          <Grid item xs={12} md={6} lg={4} key={sprint.id}>
                            <Paper sx={{ p: 2, backgroundColor: 'background.default' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Typography variant="h6" sx={{ fontSize: '1rem' }}>
                                  {sprint.name}
                                </Typography>
                                <Chip
                                  icon={getSprintStatusIcon(sprint.state)}
                                  label={sprint.state}
                                  color={getSprintStatusColor(sprint.state) as any}
                                  size="small"
                                />
                              </Box>
                              
                              {sprint.goal && (
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.875rem' }}>
                                  {sprint.goal}
                                </Typography>
                              )}

                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
                                {new Date(sprint.startDate).toLocaleDateString()} - {new Date(sprint.endDate).toLocaleDateString()}
                              </Typography>

                              {(sprint.plannedStoryPoints || 0) > 0 && (
                                <Box sx={{ mb: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={sprint.progressPercentage || 0}
                                    sx={{ mb: 0.5, height: 6 }}
                                  />
                                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                    {sprint.progressPercentage || 0}% ({sprint.completedStoryPoints || 0}/{sprint.plannedStoryPoints} SP)
                                  </Typography>
                                </Box>
                              )}

                              {sprint.board && (
                                <Box sx={{ mb: 1 }}>
                                  <Chip
                                    icon={<AssignmentIcon />}
                                    label={`Board: ${sprint.board.name}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.7rem' }}
                                  />
                                </Box>
                              )}

                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleViewSprintDetails(sprint)}
                                  sx={{ fontSize: '0.75rem' }}
                                >
                                  Details
                                </Button>
                                {sprint.state === 'planning' && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleStartSprint(sprint.id)}
                                    sx={{ fontSize: '0.75rem' }}
                                  >
                                    Start
                                  </Button>
                                )}
                                {sprint.board && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => window.location.href = `/board/${project.id}?boardId=${sprint.board!.id}`}
                                    sx={{ fontSize: '0.75rem' }}
                                  >
                                    View Board
                                  </Button>
                                )}
                              </Box>
                            </Paper>
                          </Grid>
                            ))}
                          </Grid>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </>
        )}

      {/* Create Sprint Modal */}
      <Dialog open={openCreateSprintModal} onClose={handleCloseCreateSprintModal} maxWidth="md" fullWidth>
        <DialogTitle>Create New Sprint</DialogTitle>
        <DialogContent>
          {!projectId && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Project</InputLabel>
              <Select
                value={selectedProjectIdForCreation}
                label="Project"
                onChange={(e) => {
                  const pid = e.target.value as string;
                  setSelectedProjectIdForCreation(pid);
                  setSprintForm(prev => ({ ...prev, epicIds: [] }));
                  loadFeaturesForProject(pid);
                }}
              >
                <MenuItem value=""><em>Select a project</em></MenuItem>
                {(allProjects || []).map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            fullWidth
            label="Sprint Name"
            value={sprintForm.name}
            onChange={(e) => setSprintForm(prev => ({ ...prev, name: e.target.value }))}
            sx={{ mb: 2, mt: 1 }}
          />
          <FormControl fullWidth sx={{ mb: 2 }} disabled={!projectId && !selectedProjectIdForCreation}>
            <InputLabel>Features (Optional)</InputLabel>
            <Select
              multiple
              value={sprintForm.epicIds}
              onChange={(e) => setSprintForm(prev => ({ ...prev, epicIds: e.target.value as string[] }))}
              label="Features (Optional)"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((epicId) => {
                    const feature = features.find(f => f.id === epicId);
                    return feature ? (
                      <Chip key={epicId} label={feature.name} size="small" />
                    ) : null;
                  })}
                </Box>
              )}
            >
              {!projectId && !selectedProjectIdForCreation ? (
                <MenuItem disabled>
                  <em>Select a project first</em>
                </MenuItem>
              ) : features.length === 0 ? (
                <MenuItem disabled>
                  <em>No features available. Create features first.</em>
                </MenuItem>
              ) : (
                features.map((feature) => (
                  <MenuItem key={feature.id} value={feature.id}>
                    <Checkbox checked={sprintForm.epicIds.indexOf(feature.id) > -1} />
                    <MuiListItemText primary={feature.name} />
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Goal"
            multiline
            rows={2}
            value={sprintForm.goal}
            onChange={(e) => setSprintForm(prev => ({ ...prev, goal: e.target.value }))}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={sprintForm.startDate}
                onChange={(e) => setSprintForm(prev => ({ ...prev, startDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={sprintForm.endDate}
                onChange={(e) => setSprintForm(prev => ({ ...prev, endDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            label="Planned Story Points"
            type="number"
            value={sprintForm.plannedStoryPoints}
            onChange={(e) => setSprintForm(prev => ({ ...prev, plannedStoryPoints: parseInt(e.target.value) || 0 }))}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateSprintModal}>Cancel</Button>
          <Button onClick={handleSaveSprint} variant="contained" disabled={createSprintLoading || (!projectId && !selectedProjectIdForCreation)}>
            {createSprintLoading ? <CircularProgress size={20} /> : 'Create Sprint'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sprint Details Modal */}
      <Dialog 
        open={openSprintDetailsModal} 
        onClose={() => {
          setOpenSprintDetailsModal(false);
          setSprintDetailsTab(0);
        }} 
        maxWidth="lg" 
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6">
              {selectedSprint?.name} - Sprint Details
            </Typography>
            <Tabs 
              value={sprintDetailsTab} 
              onChange={(_, newValue) => setSprintDetailsTab(newValue)}
              sx={{ minHeight: 'auto' }}
            >
              <Tab label="Overview" sx={{ minHeight: 'auto', py: 1 }} />
              <Tab label="Timeline View" sx={{ minHeight: 'auto', py: 1 }} />
            </Tabs>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedSprint && (
            <Box>
              {/* Overview Tab */}
              {sprintDetailsTab === 0 && (
                <>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {selectedSprint.name}
                  </Typography>
                  
                  {selectedSprint.goal && (
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      <strong>Goal:</strong> {selectedSprint.goal}
                    </Typography>
                  )}

                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Duration:</strong> {new Date(selectedSprint.startDate).toLocaleDateString()} - {new Date(selectedSprint.endDate).toLocaleDateString()}
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 2 }}>
                    <strong>Status:</strong> {selectedSprint.state}
                  </Typography>

                  {(selectedSprint.plannedStoryPoints || 0) > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Progress:</strong> {selectedSprint.progressPercentage || 0}% ({selectedSprint.completedStoryPoints || 0}/{selectedSprint.plannedStoryPoints} SP)
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={selectedSprint.progressPercentage || 0}
                      />
                    </Box>
                  )}

                  {selectedSprint.board && (
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      <strong>Assigned Board:</strong> {selectedSprint.board.name}
                    </Typography>
                  )}

                  {selectedSprint.issues && selectedSprint.issues.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" sx={{ mb: 2 }}>
                        Issues ({selectedSprint.issues.length})
                      </Typography>
                      <List>
                        {selectedSprint.issues.map((issue: any) => (
                          <ListItem key={issue.id}>
                            <ListItemText
                              primary={issue.title}
                              secondary={`${issue.type} - ${issue.status}`}
                            />
                            <ListItemSecondaryAction>
                              <Chip
                                label={`${issue.storyPoints || 0} SP`}
                                size="small"
                              />
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Sprint Burndown Chart */}
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Sprint Progress
                    </Typography>
                    <SprintBurndownChart 
                      sprintId={selectedSprint.id} 
                      height={400}
                      showControls={true}
                    />
                  </Box>
                </>
              )}

              {/* Timeline View Tab */}
              {sprintDetailsTab === 1 && (
                <Box sx={{ mt: 2 }}>
                  <SprintTimelineView 
                    sprint={selectedSprint}
                    issues={selectedSprint.issues || []}
                    height={600}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenSprintDetailsModal(false);
            setSprintDetailsTab(0);
          }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Board Assignment Modal */}
      <Dialog open={openBoardAssignmentModal} onClose={handleCloseBoardAssignmentModal} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Sprint to Board</DialogTitle>
        <DialogContent>
          {selectedSprint && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedSprint.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedSprint.goal}
              </Typography>
            </Box>
          )}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Board</InputLabel>
            <Select
              value={selectedBoardId}
              onChange={(e) => setSelectedBoardId(e.target.value)}
              label="Board"
            >
              <MenuItem value="">
                <em>No Board</em>
              </MenuItem>
              {boards.map((board) => (
                <MenuItem key={board.id} value={board.id}>
                  {board.name} ({board.type})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Assigning a sprint to a board will make it the active sprint for that board.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBoardAssignmentModal}>Cancel</Button>
          <Button 
            onClick={handleBoardAssignmentSubmit} 
            variant="contained"
            disabled={boardAssignmentLoading}
          >
            {boardAssignmentLoading ? <CircularProgress size={20} /> : 'Assign Board'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Sprints;